import Tooltip from '@mui/material/Tooltip';
import React, { createRef, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { videoApi } from 'api';
import { useModal } from 'shared/hooks';
import { TaskKeysType } from 'shared/types';
import { ItemActionModal } from 'shared/ui/ItemActionModal';

import cameraSvg from '../../../assets/img/camera_video.svg';
import cameraRedSvg from '../../../assets/img/camera_video_red.svg';

interface Props {
  currentICLId: string;
  id: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  videoUrl?: string;
  remoteTaskId?: string;
  videoFile: { file: File; url: string } | null;
  setVideoFile: (file: { file: File; url: string } | null) => void;
  hasText?: boolean;
  closeModal?: () => void;
}

export const VideoInput = ({
  currentICLId,
  id,
  handleChangeTaskByKey,
  videoUrl,
  remoteTaskId,
  videoFile,
  setVideoFile,
  hasText,
  closeModal,
}: Props) => {
  const { t } = useTranslation();
  const videoInputRef = createRef<HTMLInputElement>();
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const { isOpen: isRemovingVideoModalOpen, close: closeRemovingVideoModal, open: openRemovingVideoModal } = useModal();

  const handleAddVideoLocal = async () => {
    const videoInput = videoInputRef.current;

    if (!videoInput || !videoInput.files) {
      return;
    }

    const file = videoInput.files[0];
    setVideoFile({ file, url: URL.createObjectURL(file) });
  };

  const sendVideo = async () => {
    const videoInput = videoInputRef.current;

    if (!videoInput || !videoInput.files) {
      return;
    }

    setIsVideoLoading(true);

    const file = videoInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const gottenVideoUrl = await videoApi.addVideo(file, currentICLId, id);
      handleChangeTaskByKey(id, 'videoUrl', gottenVideoUrl);

      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsVideoLoading(false);
  };

  const removeVideo = async () => {
    setIsVideoLoading(true);

    try {
      remoteTaskId && (await videoApi.removeVideo(videoUrl ?? (videoFile?.url as string), currentICLId, id));
      handleChangeTaskByKey(id, 'videoUrl', '');
      setVideoFile(null);
      toast.success(t('Successfully removed!'));
      closeRemovingVideoModal();
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsVideoLoading(false);
  };

  return (
    <div className="ql-image button-icon">
      <Tooltip
        title={videoUrl || videoFile ? t('remove video') : t('add video')}
        componentsProps={{
          popper: {
            sx: {
              ['& .MuiTooltip-arrow']: {
                color: '#9CDFFC',
              },
              ['& .MuiTooltip-tooltip']: {
                backgroundColor: '#9CDFFC',
                color: '#000000',
                fontSize: '14px',
                padding: '14px',
                borderRadius: '8px',
              },
            },
          },
        }}
        arrow
      >
        <button
          onClick={() => {
            closeModal && closeModal();

            if (videoUrl || videoFile) {
              openRemovingVideoModal();
            } else {
              videoInputRef.current?.click();
            }
          }}
          className={
            hasText
              ? 'flex items-center gap-[10px]'
              : 'btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600'
          }
        >
          {videoUrl || videoFile ? <img src={cameraRedSvg} alt="camera" /> : <img src={cameraSvg} alt="camera" />}

          <input
            id="videoInput"
            type="file"
            className="hidden"
            accept="video/mp4,video/x-m4v,video/*"
            ref={videoInputRef}
            onChange={() => (remoteTaskId ? sendVideo() : handleAddVideoLocal())}
            disabled={isVideoLoading || !!videoUrl}
            title={t('add video')}
          />

          {hasText && (
            <p className="!mb-0 text-base font-bold text-[#242C2C]">
              {videoUrl || videoFile ? t('remove video') : t('add video')}
            </p>
          )}
        </button>
      </Tooltip>

      <ItemActionModal
        isOpen={isRemovingVideoModalOpen}
        close={closeRemovingVideoModal}
        title={t('Remove Video')}
        onHandle={removeVideo}
        isSubmitButtonDisabled={isVideoLoading}
        buttonTitle="delete"
      />
    </div>
  );
};
